import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000/api`;
// Tạo axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor để thêm token vào mỗi request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// =====================================================
// Interceptor xử lý lỗi xác thực (401/403)
// - Hiển thị thông báo rõ ràng cho user
// - Tự redirect về Login
// - Chống spam: chỉ xử lý 1 lần dù 15 API fail cùng lúc
// =====================================================
let isRedirectingToLogin = false;

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const message = error.response?.data?.message || '';

    // Token hết hạn hoặc không hợp lệ
    if (status === 401 || (status === 403 && message.toLowerCase().includes('token'))) {
      if (!isRedirectingToLogin) {
        isRedirectingToLogin = true;

        // Xóa thông tin đăng nhập cũ
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('permissions');

        // Hiện thông báo trước khi redirect
        // Tạo toast notification bằng DOM (không phụ thuộc antd message)
        const toast = document.createElement('div');
        toast.innerHTML = `
          <div style="
            position: fixed; top: 20px; left: 50%; transform: translateX(-50%);
            z-index: 99999; padding: 14px 28px;
            background: linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%);
            color: white; border-radius: 10px;
            font-size: 14px; font-weight: 600;
            box-shadow: 0 6px 20px rgba(255,77,79,0.4);
            display: flex; align-items: center; gap: 10px;
            animation: slideDown 0.3s ease-out;
          ">
            <span style="font-size: 20px;">⚠️</span>
            <div>
              <div>Phiên đăng nhập đã hết hạn</div>
              <div style="font-size: 12px; font-weight: 400; opacity: 0.9; margin-top: 2px;">
                Đang chuyển về trang đăng nhập...
              </div>
            </div>
          </div>
          <style>
            @keyframes slideDown {
              from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
              to { opacity: 1; transform: translateX(-50%) translateY(0); }
            }
          </style>
        `;
        document.body.appendChild(toast);

        // Redirect sau 1.5s để user kịp đọc
        setTimeout(() => {
          isRedirectingToLogin = false;
          window.location.href = '/login';
        }, 1500);
      }

      // Cancel tất cả request đang pending (tránh lỗi liên hoàn)
      return new Promise(() => {});
    }

    return Promise.reject(error);
  }
);

// =====================================================
// AUTH SERVICES
// =====================================================
export const authService = {
  // headers: { 'X-Cua-Ve': vé/thẻ qua cửa ẩn }
  login: (credentials, headers) => api.post('/auth/login', credentials, headers ? { headers } : undefined),
  loginOtp: (data) => api.post('/auth/login-otp', data),
  loginOtpResend: (data) => api.post('/auth/login-otp/resend', data),
  register: (userData) => api.post('/auth/register', userData),
  getCurrentUser: () => api.get('/auth/me'),
};

// =====================================================
// COMPANY SERVICES
// =====================================================
export const companyService = {
  getInfo: () => api.get('/company'),
  updateInfo: (data) => api.put('/company', data),
  uploadLogo: (formData) => api.post('/company/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteLogo: () => api.delete('/company/logo'),
  uploadSignature: (formData) => api.post('/company/signature', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteSignature: () => api.delete('/company/signature'),
  uploadLoginBackground: (formData) => api.post('/company/login-background', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteLoginBackground: () => api.delete('/company/login-background'),
};

// =====================================================
// USER SERVICES
// =====================================================
export const userService = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// =====================================================
// PRODUCT SERVICES
// =====================================================
export const productService = {
  getAll: (params) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  addImage: (id, image_url) => api.post(`/products/${id}/images`, { image_url }),
  removeImage: (id, index) => api.delete(`/products/${id}/images/${index}`),
  delete: (id) => api.delete(`/products/${id}`),
  search: (keyword) => api.get('/products/search', { params: { keyword } }),
  exportExcel: () => api.get('/products/export'),
  importExcel: (formData) => api.post('/products/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteAll: () => api.delete('/products/delete-all'),
  // Lịch sử hàng hóa: ai thêm / sửa / xóa, lúc nào (admin + quản lý)
  lichSu: (params) => api.get('/products/lich-su', { params }),
};

// =====================================================
// SUPPLIER SERVICES
// =====================================================
export const supplierService = {
  getAll: (params) => api.get('/suppliers', { params }),
  getById: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  datCongNo: (id, mua_cong_no) => api.patch(`/suppliers/${id}/cong-no`, { mua_cong_no }),
  delete: (id) => api.delete(`/suppliers/${id}`),
  deleteAll: () => api.delete('/suppliers/delete-all'),
  getStats: () => api.get('/suppliers/stats'),
  exportExcel: () => api.get('/suppliers/export', { responseType: 'blob' }),
  importExcel: (formData, mode = 'add') => api.post(`/suppliers/import?mode=${mode}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  recalculateDebts: () => api.post('/suppliers/recalculate-debts'),
};


// =====================================================
// CATEGORY SERVICES
// =====================================================
export const categoryService = {
  getAll: (params) => api.get('/categories', { params }),
  getById: (id) => api.get(`/categories/${id}`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// =====================================================
// TAX RATE SERVICES
// =====================================================
export const taxRateService = {
  getAll: () => api.get('/tax-rates'),
  getById: (id) => api.get(`/tax-rates/${id}`),
  create: (data) => api.post('/tax-rates', data),
  update: (id, data) => api.put(`/tax-rates/${id}`, data),
  delete: (id) => api.delete(`/tax-rates/${id}`),
};

// =====================================================
// PRICE LIST SERVICES
// =====================================================
export const priceListService = {
  getAll: (params) => api.get('/price-lists', { params }),
  getByProduct: (productId) => api.get(`/price-lists/product/${productId}`),
  create: (data) => api.post('/price-lists', data),
  update: (id, data) => api.put(`/price-lists/${id}`, data),
  delete: (id) => api.delete(`/price-lists/${id}`),
  bulkUpdate: (data) => api.post('/price-lists/bulk', data),
};

// =====================================================
// ORDER SERVICES
// =====================================================
export const orderService = {
  getAll: (params) => api.get('/orders', { params }),
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  update: (id, data) => api.put(`/orders/${id}`, data),
  delete: (id) => api.delete(`/orders/${id}`),
  updateStatus: (id, status) => api.patch(`/orders/${id}/status`, { status }),
};

// =====================================================
// PURCHASE ORDER SERVICES
// =====================================================
export const purchaseOrderService = {
  getAll: (params) => api.get('/purchase-orders', { params }),
  getAllIds: (params) => api.get('/purchase-orders/all-ids', { params }),
  getById: (id) => api.get(`/purchase-orders/${id}`),
  create: (data) => api.post('/purchase-orders', data),
  update: (id, data) => api.put(`/purchase-orders/${id}`, data),
  delete: (id) => api.delete(`/purchase-orders/${id}`),
  updateStatus: (id, status) => api.patch(`/purchase-orders/${id}/status`, { status }),
  
  // ⭐ MỚI: Lấy thông tin bán/nhập trong ngày của sản phẩm
  getProductDaySummary: (productId, date) => api.get('/purchase-orders/product-day-summary', {
    params: { product_id: productId, date }
  }),
  // Danh dau tra du tien nhanh
  markPaid: (id, data) => api.put(`/purchase-orders/${id}/mark-paid`, data || {}),
};

// =====================================================
// TRANSACTION SERVICES
// =====================================================
export const transactionService = {
  getAll: (params) => api.get('/transactions', { params }),
  getById: (id) => api.get(`/transactions/${id}`),
  create: (data) => api.post('/transactions', data),
  update: (id, data) => api.put(`/transactions/${id}`, data),
  delete: (id) => api.delete(`/transactions/${id}`),
  getStats: (params) => api.get('/transactions/stats', { params }),
  getCategories: () => api.get('/transactions/categories'),
  getCustomerDebts: (params) => api.get('/transactions/customer-debts', { params }),
  getSupplierDebts: (params) => api.get('/transactions/supplier-debts', { params }),
  getUnpaidOrders: (params) => api.get('/transactions/unpaid-orders', { params }),
  getUnpaidPurchaseOrders: (params) => api.get('/transactions/unpaid-purchase-orders', { params }),
  signTransaction: (id, data) => api.put(`/transactions/${id}/sign`, data),
  reallocate: (id) => api.post(`/transactions/${id}/reallocate`),
  // Đối chiếu & tính lại "đã thu" của đơn bán theo phiếu thu (apply=false: xem trước)
  recomputeCustomer: (customerId, apply) => api.post(`/transactions/recompute-customer/${customerId}`, { apply: !!apply }),
  // === MỚI: Chi cho nhân viên ===
  getEmployees: (params) => api.get('/transactions/employees', { params }),
  getMyPendingReceipts: () => api.get('/transactions/my-pending-receipts'),
  confirmReceipt: (id, data) => api.put(`/transactions/${id}/confirm-receipt`, data),
  rejectReceipt: (id, data) => api.put(`/transactions/${id}/reject-receipt`, data),
  // Ảnh phiếu
  uploadImage: (formData) => api.post('/transactions/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteImage: (id, imageIndex) => api.delete(
    `/transactions/${id}/image${imageIndex !== undefined ? `?imageIndex=${imageIndex}` : ''}`
  ),
};

// =====================================================
// REPORT SERVICES
// =====================================================
export const reportService = {
  getSalesReport: (params) => api.get('/reports/sales', { params }),
  getPurchaseReport: (params) => api.get('/reports/purchases', { params }),
  getInventoryReport: (params) => api.get('/reports/inventory', { params }),
  getProfitReport: (params) => api.get('/reports/profit', { params }),
  getCustomerDebtReport: (params) => api.get('/reports/customer-debt', { params }),
  getSupplierDebtReport: (params) => api.get('/reports/supplier-debt', { params }),
  getPurchaseSettlement: (params) => api.get('/reports/purchase-settlement', { params }),
  setWalletStart: (date) => api.post('/reports/purchase-settlement/wallet-start', { date }),
  getBuyers: () => api.get('/reports/buyers'),
};

// =====================================================
// INVENTORY REPORT SERVICES
// =====================================================
export const inventoryReportService = {
  getSummary: (params) => api.get('/reports/inventory/summary', { params }),
  getByCategory: () => api.get('/reports/inventory/by-category'),
  getTopProducts: (params) => api.get('/reports/inventory/top-products', { params }),
  getMovement: (params) => api.get('/reports/inventory/movement', { params }),
  exportExcel: (params) => api.get('/reports/inventory/export', { 
    params, 
    responseType: 'blob' 
  })
};

// =====================================================
// UPLOAD SERVICES
// =====================================================
export const uploadService = {
  uploadProductImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/upload/product', formData, {
      headers: {
        'Content-Type': undefined,
      },
      transformRequest: [(data) => data],
    });
  },
  deleteProductImage: (filename) => api.delete(`/upload/product/${filename}`),
};

// =====================================================
// CUSTOMER SERVICE
// =====================================================
export const customerService = {
  getAll: (params) => api.get('/customers', { params }),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  deleteAll: () => api.delete('/customers/delete-all'),
  exportExcel: () => api.get('/customers/export', { responseType: 'blob' }),
  importExcel: (formData) => api.post('/customers/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getStats: () => api.get('/customers/stats'),
  getTransactions: (id, params) => api.get(`/customers/${id}/transactions`, { params })
};

// =====================================================
// SALES ORDER SERVICE
// =====================================================
export const salesOrderService = {
  updateStatus: (id, status) => api.patch(`/sales-orders/${id}/status`, { status }),
  getCompanyInfo: () => api.get('/sales-orders/company-info'),
  updateCompanyInfo: (data) => api.put('/sales-orders/company-info', data),
  searchProducts: (q) => api.get('/sales-orders/search-products', { params: { q, limit: 20 } }),
  searchCustomers: (q) => api.get('/sales-orders/search-customers', { params: { q, limit: 10 } }),
  generateOrderCode: () => api.get('/sales-orders/generate-code'),
  // Smart: lấy danh sách sản phẩm khách hàng hay mua (6 tháng) - chỉ call 1 lần/khách
  getCustomerFrequentProducts: (customerId) => api.get(`/sales-orders/customer-frequent-products/${customerId}`),
  getStats: (params) => api.get('/sales-orders/stats', { params }),
  getAll: (params) => api.get('/sales-orders', { params }),
  getById: (id) => api.get(`/sales-orders/${id}`),
  create: (data) => api.post('/sales-orders', data),
  update: (id, data) => api.put(`/sales-orders/${id}`, data),
  delete: (id) => api.delete(`/sales-orders/${id}`),
  updateInvoiceNumber: (id, invoice_number) => api.patch(`/sales-orders/${id}/invoice-number`, { invoice_number }),
  uploadImage: (formData) => api.post('/sales-orders/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteImage: (orderId, imageIndex) => api.delete(`/sales-orders/${orderId}/image${imageIndex !== undefined ? `?imageIndex=${imageIndex}` : ''}`),
  exportSimple: (params) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/sales-orders/export?${queryString}`, {
      responseType: 'blob'
    });
  },
  exportDetail: (params) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/sales-orders/export-detail?${queryString}`, {
      responseType: 'blob'
    });
  },
  // THÊM 2 DÒNG NÀY:
  importOrders: async (formData) => {
    const response = await fetch(`${API_URL}/sales-orders/import-orders`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: formData
    });
    const data = await response.json();
    if (!response.ok) {
      const error = new Error(data.message || 'Import failed');
      error.response = { data };
      throw error;
    }
    return { data };
  },
  downloadImportTemplate: () => api.get('/sales-orders/import-orders-template', { responseType: 'blob' }),
};

// =====================================================
// PRICE LIST REPORT SERVICES (BẢNG GIÁ) - CHỈ 1 LẦN
// =====================================================
export const priceListReportService = {
  // Lấy bảng giá theo level (1-5)
  getByLevel: (params) => api.get('/price-list-report', { params }),
  
  // Lấy TẤT CẢ sản phẩm với 5 loại giá (để filter local - NHANH)
  getAllWithPrices: () => api.get('/price-list-report/all-prices'),
  
  // Lấy danh mục
  getCategories: () => api.get('/price-list-report/categories'),

  // Lấy danh sách ID sản phẩm "lâu không bán" (không có đơn bán trong N tháng)
  getDeadProducts: (months) => api.get('/price-list-report/dead-products', { params: { months } }),

  // Xuất Excel (có thêm month, year)
  exportExcel: (params) => api.get('/price-list-report/export', {
    params,
    responseType: 'blob'
  }),

  // Xuất PDF (tạo file PDF thẳng ở server, đủ SP)
  exportPDF: (params) => api.get('/price-list-report/export-pdf', { params, responseType: 'blob' })
};
// =====================================================
// DASHBOARD SERVICES
// =====================================================
export const dashboardService = {
  getStats: (params) => api.get('/dashboard/stats', { params }),
  getRevenueChart: (params) => api.get('/dashboard/revenue-chart', { params }),
  getCategoryChart: (params) => api.get('/dashboard/category-chart', { params }),
  getRecentOrders: (params) => api.get('/dashboard/recent-orders', { params }),
  getTopDebtCustomers: (params) => api.get('/dashboard/top-debt-customers', { params }),
  getTopSellingProducts: (params) => api.get('/dashboard/top-selling-products', { params }),
  getLowStock: (params) => api.get('/dashboard/low-stock', { params }),
  getSupplierDebt: () => api.get('/dashboard/supplier-debt'),
  getOverdueCustomers: () => api.get('/dashboard/overdue-customers'),
  getLosingProducts: (params) => api.get('/reports/summary/losing-products', { params }),
  getTransactionStats: (params) => api.get('/dashboard/transaction-stats', { params }),
};
// =====================================================
// DEBT REMINDER SERVICES - Nhắc nợ
// =====================================================
export const debtReminderService = {
  getOverdueList: (params) => api.get('/debt-reminder', { params }),
  exportPDF: (customerId) => api.get(`/debt-reminder/pdf/${customerId}`, { responseType: 'blob' }),
  exportCompanyPDF: (tax) => api.get(`/debt-reminder/pdf-company`, { params: { tax }, responseType: 'blob' }),
};
// =====================================================
// PROFIT REPORT SERVICES - Báo cáo Lãi/Lỗ
// =====================================================
export const profitReportService = {
  // Theo mã hàng
  getReport: (params) => api.get('/profit-report', { params }),
  
  // Theo đơn hàng
  getByOrder: (params) => api.get('/profit-report/by-order', { params }),
  
  // Theo khách hàng
  getByCustomer: (params) => api.get('/profit-report/by-customer', { params }),
  
  // Theo nhóm hàng
  getByCategory: (params) => api.get('/profit-report/by-category', { params }),
  
  // Sản phẩm bán lỗ (cho Marquee Dashboard)
  getLosingProducts: (params) => api.get('/profit-report/losing-products', { params }),
  
  // Chi tiết đơn hàng
  getOrderDetail: (id) => api.get(`/profit-report/order/${id}`),
  
  // Export & Filter
  exportExcel: (params) => api.get('/profit-report/export-excel', { params, responseType: 'blob' }),
  exportPDF: (params) => api.get('/profit-report/export-pdf', { params, responseType: 'blob' }),
  getCategories: () => api.get('/profit-report/categories'),
  getSuppliers: () => api.get('/profit-report/suppliers'),
  getCustomers: () => api.get('/profit-report/customers'),
  // Trong profitReportService
exportExcel: (params) => api.get('/profit-report/export', { 
  params, 
  responseType: 'blob'
}),
};
// =====================================================
// DISCOUNT SERVICES - Chiết khấu khách hàng
// =====================================================
export const discountService = {
  getEligible: (params) => api.get('/discount/eligible', { params }),
  apply: (data) => api.post('/discount/apply', data),
  uploadImage: (formData) => api.post('/discount/upload-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getHistory: (params) => api.get('/discount/history', { params }),
  markPaid: (data) => api.post('/discount/mark-paid', data),
  updateAmount: (id, amount) => api.put(`/discount/${id}/amount`, { amount }),
  remove: (id) => api.delete(`/discount/${id}`),
  listCustomers: () => api.get('/discount/customers'),
  toggle: (data) => api.post('/discount/toggle', data),
  lock: (data) => api.post('/discount/lock', data),
  // Mức chiết khấu theo ngày hiệu lực (sửa: chỉ admin)
  getChinhSach: () => api.get('/discount/chinh-sach'),
  luuChinhSach: (data) => api.put('/discount/chinh-sach', data),
};
// =====================================================
// CONTRACT SERVICES - Hợp đồng
// =====================================================
export const contractService = {
  getAll: (params) => api.get('/contracts', { params }),
  getById: (id) => api.get(`/contracts/${id}`),
  create: (data) => api.post('/contracts', data),
  update: (id, data) => api.put(`/contracts/${id}`, data),
  delete: (id) => api.delete(`/contracts/${id}`),
  getStats: () => api.get('/contracts/stats'),
  generateNumber: () => api.get('/contracts/generate-number'),
  getCompanyInfo: () => api.get('/contracts/company-info'),
  uploadFile: (id, formData) => api.post(`/contracts/${id}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  parseDocx: (formData) => api.post('/contracts/parse-docx', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getSignedFile: (id) => api.get(`/contracts/${id}/signed-file`, { responseType: 'blob' }),
  deleteFile: (id) => api.delete(`/contracts/${id}/file`),
  updateStatus: (id, status) => api.patch(`/contracts/${id}/status`, { status }),
};
// =============================================
// QUOTATION HISTORY - Lịch sử gửi báo giá
// =============================================
export const quotationHistoryService = {
  getAll: (params) => api.get('/quotation-history', { params }),
  create: (data) => api.post('/quotation-history', data),
  delete: (id) => api.delete(`/quotation-history/${id}`),
  searchCustomers: (q) => api.get('/quotation-history/search-customers', { params: { q } }),
};// =====================================================
// THÊM ĐOẠN CODE NÀY VÀO FILE api.js
// VỊ TRÍ: Sau quotationHistoryService (dòng 375), TRƯỚC dòng "export default api;"
// =====================================================

// =====================================================
// USER MANAGEMENT SERVICES - Quản lý người dùng
// =====================================================
export const userManagementService = {
  // Users
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  changePassword: (id, new_password) => api.put(`/users/${id}/password`, { new_password }),
  // Nhân viên tự đổi mật khẩu (cần mật khẩu cũ)
  changeMyPassword: (data) => api.put('/users/change-my-password', data),
  getPermissions: (id) => api.get(`/users/${id}/permissions`),

  // Roles
  getRoles: () => api.get('/users/roles/list'),
  getAllPermissions: () => api.get('/users/permissions/all'),
  getRolePermissions: (code) => api.get(`/users/roles/${code}/permissions`),
  updateRolePermissions: (code, permission_ids) => api.put(`/users/roles/${code}/permissions`, { permission_ids }),
};
// Thêm cuối file
export const costSummaryService = {
  getList: (params) => api.get('/cost-summary', { params }),
  getCategories: () => api.get('/cost-summary/categories'),
  updateManual: (data) => api.post('/cost-summary/manual', data),
  deleteManual: (productId) => api.delete(`/cost-summary/manual/${productId}`),
  bulkDeleteManual: (productIds) => api.post('/cost-summary/manual/bulk-delete', { product_ids: productIds }),
  getManualProductIds: (params) => api.get('/cost-summary/manual-ids', { params }),
  exportExcel: (params) => api.get('/cost-summary/export', { params, responseType: 'blob' })
};

// =====================================================
// QUICK QUOTE - Báo giá nhanh từ file khách
// =====================================================
// Cổng khách hàng - phía admin (bật/tắt cổng, cấp PIN)
export const portalAdminService = {
  getStatus: (customerId) => api.get(`/portal/admin/${customerId}/status`),
  enable: (customerId) => api.post(`/portal/admin/${customerId}/enable`),
  resetPin: (customerId) => api.post(`/portal/admin/${customerId}/reset-pin`),
  disable: (customerId) => api.post(`/portal/admin/${customerId}/disable`),
  listReconciliations: (params) => api.get('/portal/admin/reconciliations', { params }),
  resolveReconciliation: (id, data) => api.post(`/portal/admin/reconciliations/${id}/resolve`, data),
  deleteReconciliation: (id) => api.delete(`/portal/admin/reconciliations/${id}`),
  getAccessLog: (params) => api.get('/portal/admin/access-log', { params }),
  getReconciliationOverview: (params) => api.get('/portal/admin/reconciliation-overview', { params }),
  listLockedPeriods: (customerId) => api.get(`/portal/admin/${customerId}/locked-periods`),
  lockPeriod: (customerId, data) => api.post(`/portal/admin/${customerId}/lock-period`, data),
  unlockPeriod: (id) => api.delete(`/portal/admin/locked-periods/${id}`)
};

export const quickQuoteService = {
  match: (items, price_level) => api.post('/quick-quote/match', { items, price_level }),
  search: (q, level) => api.get('/quick-quote/search', { params: { q, level } }),
  exportQuote: (data) => api.post('/quick-quote/export', data, { responseType: 'blob' }),
  learn: (mappings) => api.post('/quick-quote/learn', { mappings }),
  getHistory: (params) => api.get('/quick-quote/history', { params }),
  deleteHistory: (id) => api.delete(`/quick-quote/history/${id}`)
};
// SHOPPING LIST SERVICES - Đơn đi chợ
// =====================================================
export const shoppingListService = {
  generate: (params) => api.get('/shopping-list/generate', { params }),
  compare: (params) => api.get('/shopping-list/compare', { params }),
  changes: (id) => api.get(`/shopping-list/${id}/changes`),
  save: (data) => api.post('/shopping-list/save', data),
  getHistory: (params) => api.get('/shopping-list/history', { params }),
  getDetail: (id) => api.get(`/shopping-list/detail/${id}`),
  delete: (id) => api.delete(`/shopping-list/${id}`),
  exportExcel: (params) => api.get('/shopping-list/export', { params, responseType: 'blob' })
};
// =====================================================
// PRICE UPDATE SERVICES - Cập nhật bảng giá
// =====================================================
export const priceUpdateService = {
  getAllPeriods: () => api.get('/price-update/periods'),
  getPeriodById: (id) => api.get(`/price-update/periods/${id}`),
  createPeriod: (data) => api.post('/price-update/periods', data),
  updatePeriod: (id, data) => api.put(`/price-update/periods/${id}`, data),
  deletePeriod: (id) => api.delete(`/price-update/periods/${id}`),
  loadCostPrices: (id) => api.post(`/price-update/periods/${id}/load-costs`),
  calculatePrices: (id) => api.post(`/price-update/periods/${id}/calculate`),
  finalizePeriod: (id) => api.post(`/price-update/periods/${id}/finalize`),
  activatePeriod: (id) => api.post(`/price-update/periods/${id}/activate`),
  getPeriodItems: (id, params) => api.get(`/price-update/periods/${id}/items`, { params }),
  updatePeriodItem: (periodId, itemId, data) => api.put(`/price-update/periods/${periodId}/items/${itemId}`, data),
  exportPeriod: (id) => api.get(`/price-update/periods/${id}/export`, { responseType: 'blob' }),
  
  // CẢNH BÁO BIẾN ĐỘNG GIÁ
  getPriceAlerts: (periodId, params) => api.get(`/price-update/periods/${periodId}/alerts`, { params }),
};
// =====================================================
// ACTIVITY LOG SERVICE
// =====================================================
export const activityLogService = {
  getLogs: (params) => api.get('/activity-logs', { params }),
  getStats: () => api.get('/activity-logs/stats'),
  getLogsByUser: (userId, params) => api.get(`/activity-logs/user/${userId}`, { params }),
  getLogDetail: (id) => api.get(`/activity-logs/${id}`),
  cleanup: (days) => api.post('/activity-logs/cleanup', { days })
};
export const addendumService = {
  getByContractId: (contractId) => api.get(`/contracts/${contractId}/addendum`),
  create: (contractId, data) => api.post(`/contracts/${contractId}/addendum`, data),
  delete: (id) => api.delete(`/addendums/${id}`)
};
export const goodsReportService = {
  getSummary: (params) => api.get('/goods-report/summary', { params }),
  getSalesDetail: (params) => api.get('/goods-report/sales-detail', { params }),
  getPurchaseDetail: (params) => api.get('/goods-report/purchase-detail', { params }),
  getByCustomer: (params) => api.get('/goods-report/by-customer', { params }),
  getBySupplier: (params) => api.get('/goods-report/by-supplier', { params }),
  getTopChart: (params) => api.get('/goods-report/top-chart', { params }),
};

// =====================================================
// SMART SHOPPING SERVICES - Đi Chợ Thông Minh
// =====================================================
export const invoiceService = {
  getBranches: (customer_id) => api.get('/invoice/branches', { params: { customer_id } }),
  getDailyUninvoiced: (from, to) => api.get('/invoice/daily-uninvoiced', { params: { from, to } }),
  getDailySettings: (search) => api.get('/invoice/daily-settings', { params: { search } }),
  setDailyFlag: (customer_id, daily) => api.post('/invoice/daily-settings', { customer_id, daily }),
};
export const smartShoppingService = {
  getShoppingList: (date) => api.get(`/smart-shopping/list/${date}`),
  getMissingDays: (from, to) => api.get('/smart-shopping/missing-days', { params: { from, to } }),
  excludeProduct: (product_id, exclude) => api.post('/smart-shopping/exclude-product', { product_id, exclude }),
  getHiddenProducts: () => api.get('/smart-shopping/hidden-products'),
  stockSkip: (date, product_id, skip) => api.post('/smart-shopping/stock-skip', { date, product_id, skip }),
  supplierOverride: (date, product_id, supplier_id) => api.post('/smart-shopping/supplier-override', { date, product_id, supplier_id }),
  searchSuppliers: (q) => api.get('/smart-shopping/search-suppliers', { params: { q } }),
  createOrders: (data) => api.post('/smart-shopping/create-orders', data),
};

// =====================================================
// Biên bản bàn giao & nghiệm thu
// =====================================================
export const acceptanceService = {
  preview: (order_ids) => api.post('/acceptance/preview', { order_ids }),
  nextNumber: () => api.get('/acceptance/next-number'),
  create: (data) => api.post('/acceptance', data),
  getAll: () => api.get('/acceptance'),
  remove: (id) => api.delete(`/acceptance/${id}`),
  exportWord: (data) => api.post('/acceptance/export/word', data, { responseType: 'blob' }),
  exportPdf: (data) => api.post('/acceptance/export/pdf', data, { responseType: 'blob' }),
};

// =====================================================
// Link báo giá có theo dõi (chống lừa đảo)
// =====================================================
export const quoteLinkService = {
  create: (data) => api.post('/quote-links', data),
  getAll: (params) => api.get('/quote-links', { params }),
  getViews: (id) => api.get(`/quote-links/${id}/views`),
  insights: (params) => api.get('/quote-links/insights', { params }),
  resend: (id) => api.post(`/quote-links/${id}/resend`),
  thangHienHanh: () => api.get('/quote-links/thang-hien-hanh'),
};

// =====================================================
// Hồ sơ công ty (GPKD, VSATTP, giấy tờ pháp lý)
// =====================================================
export const contractSignService = {
  createSession: (id, data) => api.post(`/contracts/${id}/sign-session`, data),
  cancelSession: (id) => api.delete(`/contracts/${id}/sign-session`),
  signA: (id, data) => api.post(`/contracts/${id}/sign-a`, data),
  getSigned: (id) => api.get(`/contracts/${id}/signed`),
  certPdf: (id) => api.get(`/contracts/${id}/cert-pdf`, { responseType: 'blob' }),
  signLog: (id) => api.get(`/contracts/${id}/sign-log`),
  signLogPdf: (id) => api.get(`/contracts/${id}/sign-log-pdf`, { responseType: 'blob' }),
  sendMail: (id, email) => api.post(`/contracts/${id}/send-mail`, { email }),
};

export const otpSettingsService = {
  get: () => api.get('/otp-settings'),
  save: (data) => api.put('/otp-settings', data),
  test: (phone) => api.post('/otp-settings/test', { phone }),
  testMail: (email) => api.post('/otp-settings/test-mail', { email }),
};

export const companyDocService = {
  getAll: (params) => api.get('/company-docs', { params }),
  create: (formData) => api.post('/company-docs', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, formData) => api.put(`/company-docs/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  remove: (id) => api.delete(`/company-docs/${id}`),
  // Tải file qua axios (kèm token) rồi mở blob — mở URL trực tiếp sẽ bị 401
  getFile: (id) => api.get(`/company-docs/${id}/file`, { responseType: 'blob' }),
};

// =====================================================
// Kiểm kê & Chốt tồn kho
// =====================================================
export const stockTakingService = {
  getProducts: (ngay) => api.get('/stock-taking/products', { params: ngay ? { ngay } : {} }),
  apply: (data) => api.post('/stock-taking/apply', data),
  chot: (data) => api.post('/stock-taking/chot', data),
  dsDot: () => api.get('/stock-taking/dot'),
  theKho: (id, den) => api.get(`/stock-taking/the-kho/${id}`, { params: den ? { den } : {} }),
  dsHaoHut: (params) => api.get('/stock-taking/hao-hut', { params }),
  themHaoHut: (data) => api.post('/stock-taking/hao-hut', data),
  xoaHaoHut: (id) => api.delete(`/stock-taking/hao-hut/${id}`),
  dsTruTon: () => api.get('/stock-taking/tru-ton'),
  datTruTonDanhMuc: (id, bat) => api.put(`/stock-taking/tru-ton/danh-muc/${id}`, { bat }),
  datSanPham: (id, data) => api.put(`/stock-taking/tru-ton/san-pham/${id}`, data),
  exportTemplate: () => api.get('/stock-taking/export', { responseType: 'blob' }),
  importFile: (formData) => api.post('/stock-taking/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getHistory: () => api.get('/stock-taking/history'),
};
// =====================================================
// CUA AN (buc tranh mo khoa truoc man dang nhap) + xac minh may moi
// =====================================================
export const cuaService = {
  thongTin: () => api.get('/cua/thong-tin'),
  mo: (data) => api.post('/cua/mo', data),
  cauHinh: () => api.get('/cua/cau-hinh'),
  luuCauHinh: (data) => api.put('/cua/cau-hinh', data),
  thu: (data) => api.post('/cua/thu', data),
  doiPhienBan: () => api.post('/cua/doi-phien-ban'),
  taiAnh: (formData) => api.post('/cua/anh', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
};

export default api;
